/*
 * Wondermite theme script (module, loaded once from snippets/wm-head.liquid).
 * Custom elements, so sections work again after the theme editor re-renders them:
 *   wm-lens        "Look closer": a round lens travels over the hero photo with the scroll, follows the mouse on
 *                  desktop, jumps to a tap on phones, and shows a close-up of the same material inside; the note
 *                  under it changes as it travels
 *   wm-join-steps  two-step sign-up on the customer form
 *   wm-copy        copy a code
 * Plus: sheets ([.wm-sheet]) land on the page as they scroll in, headings with .wm-pencil-h get a pencil line;
 * color swatches ([data-wm-sw]) swap the photo, the label and the add button in their card; collection sort.
 * Guards: reduced motion, html.factory-lowfx (weak devices, Save-Data, in-app browsers) and "motion off" (no
 * html.wm-anim) get everything still and in place: the lens parks at its start point. Transform / opacity only.
 */

const root = document.documentElement;
const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionOn = () => root.classList.contains('wm-anim') && !reduceQuery.matches && !root.classList.contains('factory-lowfx');
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

/* ------------------------------------------------------------------ look closer: the lens */
// Catmull-Rom through the waypoints, p in 0..1
function along(pts, p) {
  const n = pts.length - 1;
  const t = clamp(p) * n;
  const i = Math.min(n - 1, Math.floor(t));
  const u = t - i;
  const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(n, i + 2)];
  const c = (a, b, cc, d) => 0.5 * (2 * b + (-a + cc) * u + (2 * a - 5 * b + 4 * cc - d) * u * u + (-a + 3 * b - 3 * cc + d) * u * u * u);
  return [c(p0[0], p1[0], p2[0], p3[0]), c(p0[1], p1[1], p2[1], p3[1])];
}

class WmLens extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.stage = this.querySelector('[data-lens-stage]');
    this.glass = this.querySelector('[data-lens-glass]');
    this.macro = this.querySelector('.wm-lens__macro');
    this.cap = this.querySelector('[data-lens-cap]');
    if (!this.stage || !this.glass) return;
    this.caps = (this.dataset.caps || '').split('|').map((c) => c.trim()).filter(Boolean);
    const sx = clamp((parseFloat(this.dataset.x) || 60) / 100, 0.1, 0.9);
    const sy = clamp((parseFloat(this.dataset.y) || 44) / 100, 0.1, 0.9);
    this.path = [[sx, sy], [0.3, 0.34], [0.36, 0.7], [0.7, 0.66], [0.78, 0.36]];
    this.pos = [sx, sy];
    this.target = [sx, sy];
    this.travel = 0;
    this.capIndex = 0;
    this.manual = null;
    this.live = motionOn();
    this.measure();
    this.place(sx, sy);
    this.classList.add('is-ready');
    this.onResize = () => {
      this.measure();
      this.place(this.pos[0], this.pos[1]);
    };
    window.addEventListener('resize', this.onResize);
    if (!this.live) return;
    this.classList.add('is-live');
    this.req = () => {
      // a tap holds the lens until the page has scrolled a little, then the scroll path takes over again
      if (this.manual && this.manual.kind === 'touch' && Math.abs(this.scrollY() - this.manual.y) > 40) this.manual = null;
      this.kick();
    };
    document.addEventListener('scroll', this.req, { passive: true, capture: true });
    this.stage.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.mouseOver = true;
      this.manual = { kind: 'mouse', at: this.toStage(e) };
      this.kick();
    });
    this.stage.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.mouseOver = false;
      this.manual = null;
      this.kick();
    });
    this.stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      this.manual = { kind: 'touch', at: this.toStage(e), y: this.scrollY() };
      this.kick();
    });
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.kick();
      });
      this.io.observe(this.stage);
    } else this.visible = true;
    this.t0 = performance.now();
    this.kick();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.req, { capture: true });
    window.removeEventListener('resize', this.onResize);
    this.io?.disconnect();
    cancelAnimationFrame(this.raf);
    this._on = false;
  }

  scrollY() {
    const pw = document.querySelector('.page-wrapper');
    return (pw && pw.scrollTop) || window.scrollY || 0;
  }

  toStage(e) {
    const r = this.stage.getBoundingClientRect();
    return [clamp((e.clientX - r.left) / r.width, 0.08, 0.92), clamp((e.clientY - r.top) / r.height, 0.1, 0.9)];
  }

  measure() {
    this.W = this.stage.clientWidth;
    this.H = this.stage.clientHeight;
    this.D = this.glass.offsetWidth || 200;
    const note = this.glass.querySelector('.wm-lens__note');
    this.noteW = note ? note.offsetWidth : 0;
    this.M = this.macro ? this.macro.offsetWidth || this.D * 3.4 : this.D * 3.4;
  }

  progress() {
    const r = this.stage.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    // 0 while the photo sits where it loads, 1 when it has scrolled most of the way out
    return clamp((vh * 0.18 - r.top) / (r.height * 0.95 + vh * 0.12));
  }

  kick() {
    if (!this.raf) this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  frame(now) {
    this.raf = 0;
    if (this.manual) this.target = this.manual.at.slice();
    else {
      const [px, py] = along(this.path, this.progress());
      const k = (now - this.t0) / 1000;
      // a slow drift while nothing else moves it, so the lens looks alive on first paint
      this.target = [px + Math.sin(k * 0.55) * 0.035, py + Math.cos(k * 0.42) * 0.03];
    }
    const ease = this.manual && this.manual.kind === 'mouse' ? 0.2 : 0.09;
    const dx = (this.target[0] - this.pos[0]) * ease;
    const dy = (this.target[1] - this.pos[1]) * ease;
    this.place(this.pos[0] + dx, this.pos[1] + dy);
    this.travel += Math.hypot(dx * this.W, dy * this.H);
    if (this.caps.length > 1 && this.travel > Math.max(260, this.W * 0.5)) {
      this.travel = 0;
      this.nextCap();
    }
    if (this.visible !== false) this.kick();
  }

  place(x, y) {
    this.pos = [x, y];
    const D = this.D;
    const tx = x * this.W - D / 2;
    const ty = y * this.H - D / 2;
    this.glass.style.transform = `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0)`;
    if (this.macro) {
      const m = this.M;
      const mx = -x * (m - D);
      const my = -y * (m - D);
      this.macro.style.transform = `translate3d(${mx.toFixed(1)}px, ${my.toFixed(1)}px, 0)`;
    }
    // the note sits right of the lens, or left when there is no room for it on the right
    const nw = this.noteW || 0;
    const right = tx + D + 18 + nw <= this.W - 4;
    const left = !right && tx - 18 - nw >= 4;
    this.glass.classList.toggle('is-left', left);
    this.glass.classList.toggle('is-below', !right && !left && y < 0.6);
    this.glass.classList.toggle('is-above', !right && !left && y >= 0.6);
    this.glass.classList.toggle('is-up', (right || left) && y > 0.72);
  }

  nextCap() {
    if (!this.cap) return;
    this.capIndex = (this.capIndex + 1) % this.caps.length;
    const text = this.caps[this.capIndex];
    this.cap.classList.add('is-out');
    setTimeout(() => {
      this.cap.textContent = text;
      this.cap.classList.remove('is-out');
      this.measure();
    }, 180);
  }
}
if (!customElements.get('wm-lens')) customElements.define('wm-lens', WmLens);

/* ------------------------------------------------------------------ sheets land, pencil lines draw */
function watchSheets(scope = document) {
  const items = scope.querySelectorAll('.wm-sheet:not([data-in]), .wm-pencil-h:not([data-in]), .wm-plain__stamp:not([data-in])');
  if (!items.length) return;
  if (!motionOn() || !('IntersectionObserver' in window)) {
    items.forEach((el) => {
      el.dataset.in = '1';
      el.classList.add('is-in');
    });
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
  );
  items.forEach((el) => {
    el.dataset.in = '1';
    io.observe(el);
  });
}
watchSheets();
document.addEventListener('shopify:section:load', (e) => watchSheets(e.target));

/* ------------------------------------------------------------------ swatches (cards, Meet the Scope, the kit) */
document.addEventListener('click', (event) => {
  const b = event.target.closest && event.target.closest('[data-wm-sw]');
  if (!b) return;
  const scope = b.closest('[data-wm-swatch-scope]') || b.parentElement;
  scope.querySelectorAll('[data-wm-sw]').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
  const label = scope.querySelector('[data-wm-sw-label]');
  if (label) label.textContent = b.dataset.label || '';
  const img = scope.querySelector('img.wm-swap');
  if (img && b.dataset.img) {
    img.removeAttribute('srcset');
    img.src = b.dataset.img;
  }
  scope.querySelectorAll('factory-add-button').forEach((f) => {
    if (b.dataset.variantId) f.dataset.variantId = b.dataset.variantId;
  });
  if (b.dataset.url) scope.querySelectorAll('a[href*="/products/"]').forEach((a) => (a.href = b.dataset.url));
});

/* ------------------------------------------------------------------ two-step sign-up */
class WmJoinSteps extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.form = this.closest('form');
    this.mail = this.querySelector('input[type="email"]');
    this.tags = this.querySelector('[data-join-tags]');
    this.error = this.querySelector('[data-join-error]');
    this.picked = new Set();
    this.querySelector('[data-join-next]')?.addEventListener('click', () => this.next());
    this.mail?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.next();
      }
    });
    this.querySelectorAll('[data-join-tag]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        if (on) this.picked.add(chip.dataset.joinTag);
        else this.picked.delete(chip.dataset.joinTag);
        this.writeTags();
      })
    );
    this.querySelector('[data-join-skip]')?.addEventListener('click', () => {
      this.picked.clear();
      this.writeTags();
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.step(2).classList.contains('is-on')) {
        event.preventDefault();
        this.next();
      }
    });
  }

  step(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  valid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((this.mail?.value || '').trim());
  }

  next() {
    if (!this.valid()) {
      if (this.error) this.error.hidden = false;
      this.mail?.setAttribute('aria-invalid', 'true');
      this.mail?.focus();
      return;
    }
    if (this.error) this.error.hidden = true;
    this.mail?.removeAttribute('aria-invalid');
    this.step(1).classList.remove('is-on');
    this.step(2).classList.add('is-on');
    this.step(2).querySelector('button')?.focus({ preventScroll: true });
  }

  writeTags() {
    if (!this.tags) return;
    this.tags.value = [this.tags.dataset.base, ...this.picked].filter(Boolean).join(', ');
  }
}
if (!customElements.get('wm-join-steps')) customElements.define('wm-join-steps', WmJoinSteps);

/* ------------------------------------------------------------------ copy a code */
class WmCopy extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button || this._on) return;
    this._on = true;
    button.addEventListener('click', () => {
      const text = this.dataset.copy || '';
      const was = button.textContent;
      const done = () => {
        button.textContent = this.dataset.done || was;
        setTimeout(() => (button.textContent = was), 1400);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('wm-copy')) customElements.define('wm-copy', WmCopy);

/* ------------------------------------------------------------------ collection sort: submit on change */
document.addEventListener('change', (event) => {
  const select = event.target.closest && event.target.closest('[data-wm-sort] select');
  if (select) select.form.submit();
});

/* the sign-up form reloads the page after posting: bring the code into view */
if (window.location.search.includes('customer_posted=true')) {
  requestAnimationFrame(() => document.querySelector('.wm-sub__done')?.focus({ preventScroll: false }));
}
